import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionRow extends HTMLElement {
  get details() {
    return this.querySelector('[data-ref="accordion-details"]');
  }
  get summary() {
    return this.details?.querySelector('summary');
  }
  #controller = new AbortController();
  #animation = null;
  #isClosing = false;
  #isExpanding = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    const summary = this.summary;
    if (summary) {
      summary.addEventListener('click', this.#handleClick, { signal });
    }
  }

  disconnectedCallback() {
    if (this.#animation) this.#animation.cancel();
    this.#controller.abort();
  }

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #handleClick = (e) => {
    const details = this.details;
    if (!details) return;
    if (this.#prefersReducedMotion()) return; // native toggle

    e.preventDefault();

    details.style.overflow = 'hidden';

    if (this.#isClosing || !details.open) {
      this.#open();
    } else if (this.#isExpanding || details.open) {
      this.#close();
    }
  };

  #open() {
    const details = this.details;
    if (!details) return;

    const startHeight = details.offsetHeight;
    details.open = true;
    const endHeight = details.offsetHeight;

    details.style.height = `${startHeight}px`;
    void details.offsetHeight; // force reflow

    if (this.#animation) this.#animation.cancel();
    this.#isExpanding = true;

    this.#animation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 320, easing: 'cubic-bezier(0.19, 1, 0.22, 1)', fill: 'forwards' }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(true);
    this.#animation.oncancel = () => { this.#isExpanding = false; };
  }

  #close() {
    const details = this.details;
    if (!details) return;
    const summary = this.summary;
    if (!summary) return;

    this.classList.add('is-closing');

    const startHeight = details.offsetHeight;
    const endHeight = summary.offsetHeight;

    if (this.#animation) this.#animation.cancel();
    this.#isClosing = true;

    this.#animation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 260, easing: 'cubic-bezier(0.19, 1, 0.22, 1)', fill: 'forwards' }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(false);
    this.#animation.oncancel = () => {
      this.#isClosing = false;
      this.classList.remove('is-closing');
    };
  }

  #onAnimationFinish(open) {
    if (this.#animation) {
      try { this.#animation.cancel(); } catch (_) { /* noop */ }
    }
    this.details.open = open;
    this.#animation = null;
    this.#isClosing = false;
    this.#isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
    this.classList.remove('is-closing');
  }
}

if (!customElements.get('accordion-row')) {
  customElements.define('accordion-row', AccordionRow);
}
